import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Pagination
 *
 * Standalone pagination component for lists, grids, and any paginated view.
 * Navy active page, gold focus ring, warm hover states.
 *
 * Usage:
 *   <Pagination
 *     currentPage={page}
 *     totalPages={20}
 *     onPageChange={setPage}
 *   />
 *
 *   <Pagination
 *     currentPage={page}
 *     totalPages={50}
 *     onPageChange={setPage}
 *     showLabel
 *     label="students"
 *     totalItems={142}
 *     pageSize={10}
 *   />
 */

export interface PaginationProps {
	/** Current page (1-indexed) */
	currentPage: number
	/** Total number of pages */
	totalPages: number
	/** Called when page changes */
	onPageChange: (page: number) => void
	/** Max page buttons to show (including ellipsis) */
	maxVisible?: number
	/** Show "Showing X–Y of Z items" label */
	showLabel?: boolean
	/** Item type name for the label */
	label?: string
	/** Total items (for label) */
	totalItems?: number
	/** Items per page (for label) */
	pageSize?: number
	/** Size */
	size?: 'sm' | 'md'
	/** Additional class */
	className?: string
}

function ChevronLeft() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="m15 18-6-6 6-6" />
		</svg>
	)
}

function ChevronRight() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="m9 18 6-6-6-6" />
		</svg>
	)
}

function getPageRange(current: number, total: number, max: number): (number | 'ellipsis')[] {
	if (total <= max) return Array.from({ length: total }, (_, i) => i + 1)

	const pages: (number | 'ellipsis')[] = []
	const sideCount = Math.floor((max - 3) / 2) // pages on each side of current

	// Always show first page
	pages.push(1)

	const start = Math.max(2, current - sideCount)
	const end = Math.min(total - 1, current + sideCount)

	if (start > 2) pages.push('ellipsis')

	for (let i = start; i <= end; i++) {
		pages.push(i)
	}

	if (end < total - 1) pages.push('ellipsis')

	// Always show last page
	if (total > 1) pages.push(total)

	return pages
}

const sizeStyles = {
	sm: { btn: 'h-7 w-7 text-xs', nav: 'h-7 w-7' },
	md: { btn: 'h-9 w-9 text-sm', nav: 'h-9 w-9' },
}

export function Pagination({
	currentPage,
	totalPages,
	onPageChange,
	maxVisible = 7,
	showLabel = false,
	label = 'items',
	totalItems,
	pageSize,
	size = 'md',
	className,
}: PaginationProps) {
	if (totalPages <= 1) return null

	const pages = getPageRange(currentPage, totalPages, maxVisible)
	const s = sizeStyles[size]
	const hasPrev = currentPage > 1
	const hasNext = currentPage < totalPages

	// Label calculation
	let labelText = ''
	if (showLabel && totalItems != null && pageSize != null) {
		const start = (currentPage - 1) * pageSize + 1
		const end = Math.min(currentPage * pageSize, totalItems)
		labelText = `Showing ${start}–${end} of ${totalItems} ${label}`
	}

	return (
		<div className={cn('flex items-center justify-between gap-4', className)}>
			{showLabel && labelText && (
				<p className="text-xs text-muted-foreground font-ui">{labelText}</p>
			)}

			<nav className="flex items-center gap-1" aria-label="Pagination">
				{/* Previous */}
				<motion.button
					type="button"
					disabled={!hasPrev}
					onClick={() => hasPrev && onPageChange(currentPage - 1)}
					aria-label="Previous page"
					className={cn(
						'inline-flex items-center justify-center rounded-md',
						'text-muted-foreground hover:text-foreground hover:bg-cream/60',
						'transition-colors duration-fast',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30',
						'disabled:opacity-30 disabled:pointer-events-none',
						s.nav,
					)}
					whileTap={hasPrev ? { scale: 0.9 } : undefined}
				>
					<ChevronLeft />
				</motion.button>

				{/* Page buttons */}
				{pages.map((page, idx) => {
					if (page === 'ellipsis') {
						return (
							<span
								key={`ellipsis-${idx}`}
								className={cn('inline-flex items-center justify-center text-muted-foreground font-ui', s.btn)}
							>
								…
							</span>
						)
					}

					const isActive = page === currentPage

					return (
						<motion.button
							key={page}
							type="button"
							onClick={() => onPageChange(page)}
							aria-current={isActive ? 'page' : undefined}
							aria-label={`Page ${page}`}
							className={cn(
								'inline-flex items-center justify-center rounded-md font-ui font-medium',
								'transition-colors duration-fast',
								'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30',
								s.btn,
								isActive
									? 'bg-navy text-cream-50 shadow-sm'
									: 'text-foreground hover:bg-cream/60',
							)}
							whileTap={{ scale: 0.9 }}
						>
							{page}
						</motion.button>
					)
				})}

				{/* Next */}
				<motion.button
					type="button"
					disabled={!hasNext}
					onClick={() => hasNext && onPageChange(currentPage + 1)}
					aria-label="Next page"
					className={cn(
						'inline-flex items-center justify-center rounded-md',
						'text-muted-foreground hover:text-foreground hover:bg-cream/60',
						'transition-colors duration-fast',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30',
						'disabled:opacity-30 disabled:pointer-events-none',
						s.nav,
					)}
					whileTap={hasNext ? { scale: 0.9 } : undefined}
				>
					<ChevronRight />
				</motion.button>
			</nav>
		</div>
	)
}
